import cuid from "cuid";
import { get, set } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineBadge,
  MdOutlineEditCalendar,
  MdOutlinePayment,
  MdOutlinePerson,
  MdOutlinePinDrop,
  MdOutlineQrCode,
  MdOutlineReplay,
  MdOutlineSouthAmerica,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import useZipCodeCities from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import {
  BANK_ACCOUNT_OWNER_TYPE,
  CONTACT_POINT_SYSTEM,
  PAYMENT_FREQUENCIES,
  PAYMENT_RECEIVER,
  PAYMENT_TYPES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";
import { setDefaultIfEmpty } from "utils/helpers/input";

const Payment = ({
  setDisabled,
  setPercent,
  isFieldRequired,
  bindField,
  initialValues,
}) => {
  const [payerZipCode, setPayerZipCode] = useState(() =>
    get(initialValues, `telecoms.payer.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`),
  );
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { cities, loading: citiesLoading } = useZipCodeCities(payerZipCode);

  const PAYMENT_PAYER = {
    [true]: () => {
      set(initialValues, "payment.payer.owner", {
        id: get(initialValues, "payment.payer.owner.id", cuid()),
        firstName: get(initialValues, "project.contact.user.firstname"),
        lastName: get(initialValues, "project.contact.user.lastname"),
        isAdherent: true,
        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      });
      set(
        initialValues,
        "telecoms.payer",
        get(initialValues, "telecoms.contact", []),
      );
      setDisabled((prev) => ({
        ...prev,
        telecoms: {
          ...prev.telecoms,
          payer: {
            firstName: true,
            lastName: true,
            [CONTACT_POINT_SYSTEM.ADDRESS]: { value: true },
            [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: true },
            [CONTACT_POINT_SYSTEM.CITY]: { value: true },
          },
        },
      }));
    },
    [false]: () => {
      set(initialValues, "payment.payer.owner", {
        id: get(initialValues, "payment.payer.owner.id", cuid()),
        isAdherent: false,
        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      });
      set(initialValues, "telecoms.payer", {});
      setDisabled((prev) => ({
        ...prev,
        telecoms: {
          ...prev.telecoms,
          payer: undefined,
        },
      }));
    },
  };

  const syncToReceiverIfSameAccount = (payerPath, value, telecomsPath) => {
    if (
      get(initialValues, "isPayerReceiver") === PAYMENT_RECEIVER.SAME_ACCOUNT
    ) {
      if (telecomsPath) {
        const receiverPath = telecomsPath.replace(
          "telecoms.payer",
          "telecoms.receiver",
        );
        set(initialValues, receiverPath, value);
      } else {
        const receiverPath = payerPath.replace(
          "payment.payer",
          "payment.receiver",
        );
        set(initialValues, receiverPath, value);
      }
    }
  };

  const paymentTypePath = "payment.type";
  const paymentTypeField = bindField(paymentTypePath, {
    label: SUBSCRIPTION.FORM.PAYMENT_TYPE.LABEL,
    required: isFieldRequired(paymentTypePath),
    onBlur: () => setPercent(initialValues),
  });

  const paymentFrequencyPath = "payment.frequency";
  const paymentFrequencyField = bindField(paymentFrequencyPath, {
    label: SUBSCRIPTION.FORM.PAYMENT_FREQUENCY.LABEL,
    required: isFieldRequired(paymentFrequencyPath),
    onBlur: () => setPercent(initialValues),
  });

  const debitDayPath = "payment.debitDay";
  const debitDayField = bindField(debitDayPath, {
    label: SUBSCRIPTION.FORM.DEBIT_DATE,
    required: isFieldRequired(debitDayPath),
    onBlur: () => setPercent(initialValues),
  });

  const ibanPath = "payment.payer.IBAN";
  const ibanField = bindField(ibanPath, {
    label: SUBSCRIPTION.FORM.IBAN.LABEL,
    required: isFieldRequired(ibanPath),
    onChange: (value) => {
      syncToReceiverIfSameAccount(ibanPath, value);
    },
    onBlur: () => setPercent(initialValues),
  });

  const bicPath = "payment.payer.BIC";
  const bicField = bindField(bicPath, {
    label: SUBSCRIPTION.FORM.BIC.LABEL,
    required: isFieldRequired(bicPath),
    onChange: (value) => {
      syncToReceiverIfSameAccount(bicPath, value);
    },
    onBlur: () => setPercent(initialValues),
  });

  const isAdherentPath = "payment.payer.owner.isAdherent";
  const isAdherentField = bindField(isAdherentPath, {
    label: SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER.LABEL,
    required: isFieldRequired(isAdherentPath),
    disabled: get(
      initialValues,
      "insurancePlan.fields.payment.shouldBeAdherent",
    ),
    onChange: (value) => {
      setDisabled((prev) => ({
        ...prev,
        proof: {
          ...prev.proof,
          payerCin: value,
        },
      }));
      PAYMENT_PAYER[value]();
    },
    onBlur: () => setPercent(initialValues),
  });

  const socialReasonPath = "payment.payer.owner.socialReason";
  const socialReasonField = bindField(socialReasonPath, {
    label:
      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER.ORGANIZATION
        .SOCIAL_REASON,
    required: isFieldRequired(socialReasonPath),
    onBlur: () => setPercent(initialValues),
  });

  const firstNamePath = "payment.payer.owner.firstName";
  const firstNameField = bindField(firstNamePath, {
    label:
      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER.IDENTITY.FIRST_NAME
        .LABEL,
    required: isFieldRequired(firstNamePath),
    onChange: (value) => {
      syncToReceiverIfSameAccount(firstNamePath, value);
    },
    onBlur: () => setPercent(initialValues),
  });

  const lastNamePath = "payment.payer.owner.lastName";
  const lastNameField = bindField(lastNamePath, {
    label:
      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER.IDENTITY.LAST_NAME
        .LABEL,
    required: isFieldRequired(lastNamePath),
    onChange: (value) => {
      syncToReceiverIfSameAccount(lastNamePath, value);
    },
    onBlur: () => setPercent(initialValues),
  });

  const payerAddressPath = `telecoms.payer.${CONTACT_POINT_SYSTEM.ADDRESS}.value`;
  const payerAddressField = bindField(payerAddressPath, {
    label: SUBSCRIPTION.FORM.ADDRESS.LABEL,
    required: isFieldRequired(payerAddressPath),
    onChange: (value) => {
      syncToReceiverIfSameAccount(null, value, payerAddressPath);
    },
    onBlur: () => setPercent(initialValues),
  });

  const payerZipCodePath = `telecoms.payer.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`;
  const payerZipCodeField = bindField(payerZipCodePath, {
    label: SUBSCRIPTION.FORM.POSTAL_CODE.LABEL,
    required: isFieldRequired(payerZipCodePath),
    onChange: (value) => {
      syncToReceiverIfSameAccount(null, value, payerZipCodePath);
      set(
        initialValues,
        `telecoms.payer.${CONTACT_POINT_SYSTEM.CITY}.value`,
        undefined,
      );
      setPayerZipCode(value);
    },
    onBlur: () => setPercent(initialValues),
  });

  const payerCityPath = `telecoms.payer.${CONTACT_POINT_SYSTEM.CITY}.value`;
  const payerCityField = bindField(payerCityPath, {
    label: SUBSCRIPTION.FORM.CITY.LABEL,
    required: isFieldRequired(payerCityPath),
    onChange: (value) => {
      syncToReceiverIfSameAccount(null, value, payerCityPath);
    },
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PAYMENT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.TITLE}
      >
        <div className="grid--1">
          <FormField
            path={paymentTypeField.path}
            label={paymentTypeField.label}
            required={paymentTypeField.required}
            disabled={paymentTypeField.disabled}
          >
            {(field) => (
              <Input
                type="radio"
                variant="group"
                icon={<MdOutlinePayment size={16} />}
                label={paymentTypeField.label}
                values={setDefaultIfEmpty(
                  get(initialValues, "insurancePlan.fields.payment.type"),
                  [PAYMENT_TYPES.DEBIT],
                ).map((value) => ({
                  value,
                  label: SUBSCRIPTION.FORM.PAYMENT_TYPE.OPTIONS[value],
                }))}
                value={get(initialValues, paymentTypePath)}
                {...field}
                onBlur={paymentTypeField.onBlur}
                onChange={paymentTypeField.onChange}
              />
            )}
          </FormField>
          <FormField
            path={paymentFrequencyField.path}
            label={paymentFrequencyField.label}
            required={paymentFrequencyField.required}
            disabled={paymentFrequencyField.disabled}
          >
            {(field) => (
              <Input
                type="radio"
                variant="group"
                icon={<MdOutlineReplay size={16} />}
                label={paymentFrequencyField.label}
                values={setDefaultIfEmpty(
                  get(initialValues, "insurancePlan.fields.payment.frequency"),
                  Object.values(PAYMENT_FREQUENCIES),
                ).map((value) => ({
                  value,
                  label: SUBSCRIPTION.FORM.PAYMENT_FREQUENCY.OPTIONS[value],
                }))}
                value={get(initialValues, paymentFrequencyPath)}
                {...field}
                onBlur={paymentFrequencyField.onBlur}
                onChange={paymentFrequencyField.onChange}
              />
            )}
          </FormField>
          <FormField
            path={debitDayField.path}
            label={debitDayField.label}
            required={debitDayField.required}
            disabled={debitDayField.disabled}
          >
            {(field) => (
              <Select
                options={setDefaultIfEmpty(
                  get(initialValues, "insurancePlan.fields.payment.debitDay"),
                  ["5", "12"],
                ).map((value) => ({
                  value,
                  label: value,
                }))}
                placeholder={SUBSCRIPTION.FORM.DEBIT_DATE}
                label={debitDayField.label}
                icon={<MdOutlineEditCalendar size={16} />}
                defaultValue={debitDayField.defaultValue}
                {...field}
                onChange={debitDayField.onChange}
                onBlur={debitDayField.onBlur}
              />
            )}
          </FormField>
          <FormField
            path={ibanField.path}
            label={ibanField.label}
            required={ibanField.required}
            disabled={ibanField.disabled}
          >
            {(field) => (
              <Input
                label={ibanField.label}
                icon={<MdOutlineQrCode size={16} />}
                type="custom"
                trim={true}
                uppercase={true}
                placeholder={SUBSCRIPTION.FORM.IBAN.PLACEHOLDER}
                defaultValue={ibanField.defaultValue}
                {...field}
                onChange={ibanField.onChange}
                onBlur={ibanField.onBlur}
              />
            )}
          </FormField>
          <FormField
            path={bicField.path}
            label={bicField.label}
            required={bicField.required}
            disabled={bicField.disabled}
          >
            {(field) => (
              <Input
                label={bicField.label}
                icon={<MdOutlineQrCode size={16} />}
                type="custom"
                trim={true}
                uppercase={true}
                placeholder={SUBSCRIPTION.FORM.BIC.PLACEHOLDER}
                defaultValue={bicField.defaultValue}
                {...field}
                onChange={bicField.onChange}
                onBlur={bicField.onBlur}
              />
            )}
          </FormField>
          {get(initialValues, "payment.payer.owner.type") ===
            BANK_ACCOUNT_OWNER_TYPE.PARTICULAR && (
            <FormField
              path={isAdherentField.path}
              label={isAdherentField.label}
              required={isAdherentField.required}
              disabled={isAdherentField.disabled}
            >
              {(field) => (
                <Input
                  type="radio"
                  variant="group"
                  icon={<MdOutlinePerson size={16} />}
                  value={get(initialValues, isAdherentPath)}
                  label={isAdherentField.label}
                  values={[
                    {
                      value: true,
                      label:
                        SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER
                          .OPTIONS.ADHERENT,
                    },
                    {
                      value: false,
                      label:
                        SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER
                          .OPTIONS.ANOTHER_PERSON,
                      popover:
                        get(
                          initialValues,
                          "insurancePlan.fields.payment.shouldBeAdherent",
                        ) &&
                        SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT
                          .SHOULD_BE_ADHERENT,
                    },
                  ]}
                  {...field}
                  onBlur={isAdherentField.onBlur}
                  onChange={isAdherentField.onChange}
                />
              )}
            </FormField>
          )}
          {(get(initialValues, "insurancePlan.fields.tns") ||
            !get(initialValues, "payment.payer.owner.isAdherent")) && (
            <>
              {get(initialValues, "insurancePlan.fields.tns") ? (
                <Form
                  type="highlight"
                  title={
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER
                      .ORGANIZATION.TITLE
                  }
                >
                  <div className="grid--1">
                    <FormField
                      path={socialReasonField.path}
                      label={socialReasonField.label}
                      required={socialReasonField.required}
                      disabled={socialReasonField.disabled}
                    >
                      {(field) => (
                        <Input
                          label={socialReasonField.label}
                          icon={<MdOutlineBadge size={16} />}
                          optional={SUBSCRIPTION.FORM.OPTIONAL}
                          placeholder={socialReasonField.label}
                          defaultValue={socialReasonField.defaultValue}
                          {...field}
                          onChange={socialReasonField.onChange}
                          onBlur={socialReasonField.onBlur}
                        />
                      )}
                    </FormField>
                  </div>
                </Form>
              ) : (
                <Form
                  type="highlight"
                  title={
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER
                      .IDENTITY.TITLE
                  }
                >
                  <div className="grid--1">
                    <FormField
                      path={firstNameField.path}
                      label={firstNameField.label}
                      required={firstNameField.required}
                      disabled={firstNameField.disabled}
                    >
                      {(field) => (
                        <Input
                          label={firstNameField.label}
                          icon={<MdOutlineBadge size={16} />}
                          placeholder={
                            SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT
                              .PAYER.IDENTITY.FIRST_NAME.PLACEHOLDER
                          }
                          type="custom"
                          normalizeText={true}
                          defaultValue={firstNameField.defaultValue}
                          {...field}
                          onChange={firstNameField.onChange}
                          onBlur={firstNameField.onBlur}
                        />
                      )}
                    </FormField>
                    <FormField
                      path={lastNameField.path}
                      label={lastNameField.label}
                      required={lastNameField.required}
                      disabled={lastNameField.disabled}
                    >
                      {(field) => (
                        <Input
                          label={lastNameField.label}
                          icon={<MdOutlineBadge size={16} />}
                          placeholder={
                            SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT
                              .PAYER.IDENTITY.LAST_NAME.PLACEHOLDER
                          }
                          type="custom"
                          normalizeText={true}
                          defaultValue={lastNameField.defaultValue}
                          {...field}
                          onChange={lastNameField.onChange}
                          onBlur={lastNameField.onBlur}
                        />
                      )}
                    </FormField>
                  </div>
                </Form>
              )}
              <Form
                type="highlight"
                title={
                  SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER.ADDRESS
                    .TITLE
                }
              >
                <div className="grid--1">
                  <FormField
                    path={payerAddressField.path}
                    label={payerAddressField.label}
                    required={payerAddressField.required}
                    disabled={payerAddressField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={payerAddressField.label}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.ADDRESS.PLACEHOLDER}
                        type="custom"
                        normalizeText={true}
                        defaultValue={payerAddressField.defaultValue}
                        {...field}
                        onChange={payerAddressField.onChange}
                        onBlur={payerAddressField.onBlur}
                        optional={!payerAddressField.required}
                      />
                    )}
                  </FormField>
                  <FormField
                    path={payerZipCodeField.path}
                    label={payerZipCodeField.label}
                    required={payerZipCodeField.required}
                    disabled={payerZipCodeField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={payerZipCodeField.label}
                        icon={<MdOutlinePinDrop size={16} />}
                        placeholder={SUBSCRIPTION.FORM.POSTAL_CODE.PLACEHOLDER}
                        defaultValue={payerZipCodeField.defaultValue}
                        {...field}
                        onChange={payerZipCodeField.onChange}
                        onBlur={payerZipCodeField.onBlur}
                        optional={!payerZipCodeField.required}
                      />
                    )}
                  </FormField>
                  <FormField
                    path={payerCityField.path}
                    label={payerCityField.label}
                    required={payerCityField.required}
                    disabled={payerCityField.disabled}
                  >
                    {(field) => (
                      <Select
                        icon={<MdOutlineSouthAmerica size={16} />}
                        label={payerCityField.label}
                        placeholder={SUBSCRIPTION.FORM.CITY.PLACEHOLDER}
                        showSearch
                        loading={citiesLoading}
                        options={cities}
                        defaultValue={payerCityField.defaultValue}
                        {...field}
                        onChange={payerCityField.onChange}
                        onBlur={payerCityField.onBlur}
                        optional={!payerCityField.required}
                      />
                    )}
                  </FormField>
                </div>
              </Form>
            </>
          )}
        </div>
      </Form>
    </div>
  );
};

export default Payment;
